// import { setSocketFailed, setSocketPending } from './actions';
// eslint-disable-next-line import/no-cycle
import { CHATBOT_ID } from '../../constants/chatbotId';
import { currentOpenChatIDSelector } from '../../selectors';
import { checkUsersSession } from '../../services/checkUsersSession';
import { selectSession } from '../../slices/sessionSlice';
import { setSocketStatusFailed, setSocketStatusPending } from '../../slices/socketStatusSlice';
import { ERequestApiStatus } from '../../types/appState';
import { TThunkAction } from '../../types/redux/thunk';
import { ETelemetryEvents } from '../../types/telemetry';
import { isAPIError, isBadStatusCodeNetworkError, isNetworkError, isSomeOfAPIErrors } from '../../utils/network_errors';
import { retryWithDelay } from '../../utils/retryWithDelay';
import { synchronizeChatsV2 } from '../chatList/synchronizeChatsV2';
import { updateMessages } from '../messages/updateMessages';
import { registerWsSession, registerWsSessionFailed } from '../wsSession';

const RETRIES_COUNT = 4;

/**
 * Открытие канала WS
 */
export const openEventChannel =
  (reconnect: boolean = false): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry, custom: { socket } }) => {
    if (!socket) {
      return;
    }

    const { eventChannel } = socket;

    const state = getState();
    const session = selectSession(state);

    dispatch(setSocketStatusPending());

    // Если соединение не закрыто
    // TODO: избавиться от блока
    if (eventChannel.hasInstance() && !eventChannel.isChannelClosed()) {
      await dispatch(registerWsSession());

      // Если истекла сессия (стоит пересмотреть логику)
      if (session.status !== ERequestApiStatus.Succeed && session.status !== ERequestApiStatus.Pending) {
        telemetry.increment(ETelemetryEvents.ChatWsSessionExpire);
        logger.warning('Проверка сессии. Сессия истекла', {
          domain: 'src/shared/actions/socket/openEventChannel.ts',
          message: 'Сессия истекла. Соединение открыто',
        });
        await dispatch(checkUsersSession());
      }

      return;
    }

    // Если истекла сессия (стоит пересмотреть логику)
    if (session.status !== ERequestApiStatus.Succeed && session.status !== ERequestApiStatus.Pending) {
      telemetry.increment(ETelemetryEvents.ChatWsSessionExpire);
      logger.warning('Проверка сессии. Сессия истекла', {
        domain: 'src/shared/actions/socket/openEventChannel.ts',
        message: 'Сессия истекла. Соединение закрыто',
      });
      await dispatch(checkUsersSession());
    }

    const startTime = new Date().getTime();

    try {
      // Делаем 4 попытки открыть соединение с задержкой в 1, 2, 3, 4 секунды
      await retryWithDelay(
        () => {
          if (eventChannel.isChannelClosed()) {
            return eventChannel.connectToEventChannel() as Promise<void>;
          }

          return Promise.resolve();
        },
        error => {
          if (isSomeOfAPIErrors(error)) {
            // TODO: ws errors code https://tools.ietf.org/html/rfc6455#section-7.4.1
          }

          telemetry.increment(ETelemetryEvents.ChatWsSocketError);
          logger.error('Event Channel retry connection error', {
            domain: 'src/shared/actions/socket/openEventChannel.ts',
            error: {
              ...error,
              isAPIError: isAPIError(error),
              isBadStatusCodeNetworkError: isBadStatusCodeNetworkError(error),
              isNetworkError: isNetworkError(error),
            },
          });
        },
        RETRIES_COUNT,
      );
    } catch (e) {
      dispatch(setSocketStatusFailed());
      dispatch(registerWsSessionFailed());
      logger.error('Не удалось зарегистрировать сессию по вебсокету', e);
      telemetry.increment(ETelemetryEvents.ChatWsSessionFail);

      return;
    }

    await dispatch(registerWsSession(startTime));

    if (reconnect) {
      const currentOpenChatID = currentOpenChatIDSelector(state);

      if (currentOpenChatID !== CHATBOT_ID && currentOpenChatID) {
        dispatch(updateMessages(currentOpenChatID, true));
      }

      dispatch(synchronizeChatsV2());
    }
  };
